import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ColumnHeaderFilterIcon from '../../common/ColumnHeaderFilterIcon';
import type { SortingState } from '@tanstack/react-table';
import type { SkillBadgeRecord } from '../../../types/driInsights';
import { SEARCE_COLORS } from '../../../theme';
import type { DataTableColumnDef } from '../../table/dataTableTypes';
import { parseDateString } from '../../../utils/FormatUtils';

function SkillBadgeSortFilterHeader(props: {
  label: string;
  columnId: string;
  sorting: SortingState;
  hasFilter: boolean;
  onFilterClick: (e: React.MouseEvent<HTMLElement>, columnId: string) => void;
  centerHeader?: boolean;
}) {
  const { label, columnId, sorting, hasFilter, onFilterClick, centerHeader } = props;
  const active = sorting[0]?.id === columnId;
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: 34,
        textAlign: centerHeader ? 'center' : 'left',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          verticalAlign: 'middle',
          maxWidth: '100%',
          ...(centerHeader ? { justifyContent: 'center', width: '100%' } : {}),
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            color: SEARCE_COLORS.white,
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ width: 34, height: 34, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onFilterClick(e, columnId);
            }}
            sx={{
              color: hasFilter ? SEARCE_COLORS.white : alpha(SEARCE_COLORS.white, 0.75),
              padding: 0.5,
              '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.2) },
            }}
          >
            <ColumnHeaderFilterIcon isFilterActive={hasFilter} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

/** Labels for Show/Hide columns menu (DRI Insights → Skill Badges tab). */
export const DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS: Record<string, string> = {
  employee_name: 'Employee',
  quest_name: 'Quest / skill name',
  quest_level: 'Level',
  completion_date: 'Completion date',
};

export const DR_INSIGHTS_SKILL_BADGES_SORTABLE_IDS = [
  'employee_name',
  'quest_name',
  'quest_level',
  'completion_date',
] as const;

export function compareDriInsightsSkillBadgeRows(a: SkillBadgeRecord, b: SkillBadgeRecord, columnId: string): number {
  switch (columnId) {
    case 'employee_name':
    case 'quest_name':
    case 'quest_level':
      return String(a[columnId as keyof SkillBadgeRecord] ?? '').localeCompare(
        String(b[columnId as keyof SkillBadgeRecord] ?? '')
      );
    case 'completion_date': {
      const aDate = parseDateString(String(a.completion_date ?? ''));
      const bDate = parseDateString(String(b.completion_date ?? ''));
      return (aDate?.getTime() ?? 0) - (bDate?.getTime() ?? 0);
    }
    default:
      return 0;
  }
}

export interface BuildDriInsightsSkillBadgesColumnsParams {
  sorting: SortingState;
  hasActiveFilter: (columnId: string) => boolean;
  onFilterClick: (e: React.MouseEvent<HTMLElement>, columnId: string) => void;
  onHeaderSortClick: (columnId: string) => void;
  formatDate: (dateString: string | null | undefined) => string;
}

export function buildDriInsightsSkillBadgesDataTableColumns(
  p: BuildDriInsightsSkillBadgesColumnsParams
): DataTableColumnDef<SkillBadgeRecord>[] {
  const { sorting, hasActiveFilter, onFilterClick, onHeaderSortClick, formatDate } = p;

  const headerProps = (columnId: string) =>
    ({
      onClick: () => onHeaderSortClick(columnId),
      sx: { cursor: 'pointer' as const },
    }) as const;

  const head = (id: string, label: string, center?: boolean) => (
    <SkillBadgeSortFilterHeader
      label={label}
      columnId={id}
      sorting={sorting}
      hasFilter={hasActiveFilter(id)}
      onFilterClick={onFilterClick}
      centerHeader={center}
    />
  );

  return [
    {
      id: 'employee_name',
      header: head('employee_name', 'Employee'),
      meta: {
        columnMenuLabel: DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.employee_name,
        hideable: false,
        minWidth: 160,
        headerCellProps: headerProps('employee_name'),
      },
      cell: ({ row }) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
          {row.employee_name}
        </Typography>
      ),
    },
    {
      id: 'quest_name',
      header: head('quest_name', 'Quest/Skill Name', true),
      meta: {
        columnMenuLabel: DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.quest_name,
        align: 'center',
        minWidth: 200,
        headerCellProps: headerProps('quest_name'),
      },
      cell: ({ row }) => (
        <Tooltip title={row.quest_name} arrow>
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontSize: '0.75rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 220,
            }}
          >
            {row.quest_name}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'quest_level',
      header: head('quest_level', 'Level', true),
      meta: {
        columnMenuLabel: DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.quest_level,
        align: 'center',
        minWidth: 120,
        headerCellProps: headerProps('quest_level'),
      },
      cell: ({ row }) => (
        <Chip
          label={row.quest_level}
          size="small"
          sx={{
            backgroundColor:
              row.quest_level === 'Advanced'
                ? `${SEARCE_COLORS.success}15`
                : row.quest_level === 'Intermediate'
                  ? `${SEARCE_COLORS.warning}15`
                  : `${SEARCE_COLORS.primary}15`,
            color:
              row.quest_level === 'Advanced'
                ? SEARCE_COLORS.success
                : row.quest_level === 'Intermediate'
                  ? SEARCE_COLORS.warning
                  : SEARCE_COLORS.primary,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: '24px',
          }}
        />
      ),
    },
    {
      id: 'completion_date',
      header: head('completion_date', 'Completion Date', true),
      meta: {
        columnMenuLabel: DR_INSIGHTS_SKILL_BADGES_COLUMN_MENU_LABELS.completion_date,
        align: 'center',
        minWidth: 140,
        headerCellProps: headerProps('completion_date'),
      },
      cell: ({ row }) => (
        <Tooltip title={row.completion_date} arrow>
          <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            {formatDate(row.completion_date)}
          </Typography>
        </Tooltip>
      ),
    },
  ];
}
